import { Box, Checkbox, FormControlLabel, Grid, styled, useTheme } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BasicButton, ComboSelect, RadioOption, Typography } from '../../../components/atoms';
import { useAppDispatch, useAppSelector } from '../../../common/hooks';
import { getTenantBasedConfig, updateGlobalLoaderState, updateTenantInfo } from '../../../features/global/globalSlice';
import { useCallback, useEffect, useState } from 'react';
import { getCurrencyOptions } from '../../../common/constants';
import { getTenantLabels, isStalePlanError } from '../../../utils';
import FileUploadIcon from '@mui/icons-material/FileUpload';
// import AdminUploadCompanyLogo from '../../../components/organisms/SummaryTab/AdminUploadCompanyLogo';
import { getNewPalette } from '../../../themes/newPalette';
import { validateThemeJSON } from '../../../themes/utils';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close_nocircle.svg';
import { StalePlanBanner } from '../../../components/molecules/StalePlanBanner/StalePlanBanner';

const getFileInfoFromFile = (file: File): { size: string } => {
    if (!file) return { size: '0 KB' };

    // Get file size directly from File object
    const sizeInBytes = file.size;
    const sizeInKB = sizeInBytes / 1024;
    const size = sizeInKB >= 1024 ? `${(sizeInKB / 1024).toFixed(2)} MB` : `${sizeInKB.toFixed(2)} KB`;

    return { size };
};

const getFileInfoFromBase64 = (base64: string, fileName = 'file'): { name: string; size: string } => {
    if (!base64) return { name: fileName, size: '0 KB' };

    // Split off the data URL prefix (e.g. "data:image/png;base64,") if present
    const [meta, data = ''] = base64.includes(',') ? base64.split(',') : ['', base64];

    // Derive an extension from the mime type when the name has none
    const mimeMatch = meta.match(/data:([^;]+);/);
    const mimeType = mimeMatch ? mimeMatch[1] : '';
    const extension = mimeType.includes('/') ? mimeType.split('/')[1].split('+')[0] : '';
    const name = !fileName.includes('.') && extension ? `${fileName}.${extension}` : fileName;

    // Calculate the decoded byte size from the base64 payload
    const padding = (data.match(/=+$/) || [''])[0].length;
    const sizeInBytes = data.length > 0 ? (data.length * 3) / 4 - padding : 0;
    const sizeInKB = sizeInBytes / 1024;
    const size = sizeInKB >= 1024 ? `${(sizeInKB / 1024).toFixed(2)} MB` : `${sizeInKB.toFixed(2)} KB`;

    return { name, size };
};

type BrandingFormType = {
    tenantType: string;
    currencySymbol: string;
    termsOfUse: boolean;
    eula: boolean;
    dateFormat: string;
};

const Image = styled('img')(() => ({
    width: '150px',
    height: '150px'
    // objectFit: 'contain'
}));

const StyledBox = styled(Box)(() => ({
    border: '1px solid #E6E6E6',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.05)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'background.paper',
    width: '100%',
    padding: '30px 0px',
    margin: '0 0 20px 0'
}));
// eslint-disable-next-line no-unused-vars
const FormWrapper = styled('form')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '90%'
}));

// eslint-disable-next-line no-unused-vars
const FormRow = styled(Grid)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
    width: '100%'
}));

// eslint-disable-next-line no-unused-vars
const FormField = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
}));

const Label = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '4px'
}));

const UploadArea = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: '8px',
    padding: '15px 25px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.grey[50],
    height: '240px',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary[50] || theme.palette.grey[100]
    }
}));

const UploadIcon = styled(FileUploadIcon)(({ theme }) => ({
    fontSize: '48px',
    color: theme.palette.grey[400]
}));

const UploadText = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    color: theme.palette.grey[600],
    '& span': {
        color: theme.palette.primary.main,
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline'
        }
    }
}));

const HelperText = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
    color: theme.palette.grey[500]
}));

const ButtonGroup = styled(Box)(() => ({
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-start',
    marginTop: '8px'
}));

const LogoContainer = styled('div')(() => ({
    display: 'flex',
    position: 'relative',
    top: '1px'
}));

export const AdminBrandingRevamp = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    // AEX-3363: /admin tenant-info can be edited from two tabs. On a stale rejection show the Refresh
    // banner; "Refresh" re-fetches gettenantinfo (getTenantBasedConfig) — the source of this page's
    // data — so the next save uses the fresh tenant_info_ref_id.
    const [isStale, setIsStale] = useState(false);
    const theme = useTheme();
    const { globalConfig } = useAppSelector((state) => state.global);
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const tenantLabels = getTenantLabels(t);
    const currencies = getCurrencyOptions(t);
    const { logo, secondaryLogo } = globalConfig;

    const initialFormValues: BrandingFormType = {
        tenantType: globalConfig.tenantType || 'Trial',
        currencySymbol: globalConfig.currencySymbol || 'English-US',
        termsOfUse: globalConfig.termsOfUse || false,
        eula: globalConfig.eula || false,
        dateFormat: globalConfig.dateFormat || 'MM/DD/YYYY'
    };

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset
    } = useForm<BrandingFormType>({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: initialFormValues
    });

    const [savedValues, setSavedValues] = useState(initialFormValues);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [secondaryLogoFile, setSecondaryLogoFile] = useState<File | null>(null);
    const [themeFile, setThemeFile] = useState<File | null>(null);
    const [logoBase64, setLogoBase64] = useState<string>('');
    const [secondaryLogoBase64, setSecondaryLogoBase64] = useState<string>('');
    const [themeBase64, setThemeBase64] = useState<string>('');
    const [jsonError, setJsonError] = useState<string>('');

    useEffect(() => {
        if (globalConfig) {
            const currentSavedValues = {
                tenantType: globalConfig.tenantType || 'Trial',
                currencySymbol: globalConfig.currencySymbol || 'English-US',
                termsOfUse: globalConfig.termsOfUse || false,
                eula: globalConfig.eula || false,
                dateFormat: globalConfig.dateFormat || 'MM/DD/YYYY'
            };
            setSavedValues(currentSavedValues);
            reset(currentSavedValues);
        }
    }, [globalConfig, reset]);

    useEffect(() => {
        if (!logoFile) {
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoBase64(reader.result as string);
        };
        reader.readAsDataURL(logoFile);
        return () => {
            reader.abort();
        };
    }, [logoFile]);

    useEffect(() => {
        if (!secondaryLogoFile) {
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setSecondaryLogoBase64(reader.result as string);
        };
        reader.readAsDataURL(secondaryLogoFile);
        return () => {
            reader.abort();
        };
    }, [secondaryLogoFile]);

    useEffect(() => {
        if (!themeFile) {
            setThemeBase64('');
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            try {
                const jsonContent = reader.result as string;
                if (!validateThemeJSON(jsonContent)) {
                    setJsonError(t('JSON_VALIDATION_ERROR'));
                    setThemeFile(null);
                    setThemeBase64('');
                } else {
                    setThemeBase64(jsonContent);
                    setJsonError('');
                }
            } catch (error) {
                setJsonError(t('INVALID_JSON_FILE'));
                console.error('Invalid JSON file:', error);
                setThemeFile(null);
                setThemeBase64('');
            }
        };
        reader.onerror = () => {
            console.error('Error reading file');
            setJsonError(t('ERROR_READING_FILE'));
            setThemeFile(null);
            setThemeBase64('');
        };
        reader.readAsText(themeFile);
        return () => {
            reader.abort();
        };
    }, [themeFile]);

    useEffect(() => {
        setLogoBase64(logo || '');
        setSecondaryLogoBase64(secondaryLogo || '');
    }, [logo, secondaryLogo]);

    const currencyOptions = useAppSelector((state) => {
        const orgCurrencies = state.global.orgInfo?.currencies;
        return orgCurrencies && orgCurrencies.length > 0 ? orgCurrencies : currencies;
    });

    const tenantOptions: { label: string; value: string }[] = Object.keys(tenantLabels).map((eachKey) => {
        const key = eachKey as keyof typeof tenantLabels;
        return {
            label: tenantLabels[key],
            value: eachKey
        };
    });

    const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate SVG file and size
            if (file.type !== 'image/svg+xml') {
                alert('Please upload an SVG file');
                return;
            }
            if (file.size > 200 * 1024) {
                alert('File size must be less than 200KB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoFile(file);
                setLogoBase64((reader.result as string) || '');
            };
            reader.readAsDataURL(file);
        }
        event.target.value = '';
    };

    const handleThemeUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate JSON file
            if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
                alert('Please upload a JSON file');
                return;
            }
            setThemeFile(file);
        }
    };

    const handleSecondaryLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.type !== 'image/svg+xml') {
                alert('Please upload an SVG file');
                return;
            }
            if (file.size > 200 * 1024) {
                alert('File size must be less than 200KB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setSecondaryLogoFile(file);
                setSecondaryLogoBase64((reader.result as string) || '');
            };
            reader.readAsDataURL(file);
        }
        event.target.value = '';
    };

    // AEX-3363: refresh this page's data from gettenantinfo so the next save uses a fresh ref id.
    const refreshTenantInfo = useCallback(async () => {
        try {
            dispatch(updateGlobalLoaderState(true));
            await dispatch(
                getTenantBasedConfig({ tenantId: tenant || '', i18n, isLoader: false, callCMS: false })
            ).unwrap();
            setIsStale(false);
        } catch {
            // keep the banner so the advisor can retry
        } finally {
            dispatch(updateGlobalLoaderState(false));
        }
    }, [dispatch, tenant, i18n]);

    const onSubmit = async (data: BrandingFormType) => {
        if (!logoBase64) {
            return;
        }
        dispatch(updateGlobalLoaderState(true));
        const requestedPayload = {
            ...globalConfig,
            subdomain: tenant,
            tenantType: data.tenantType,
            currencySymbol: data.currencySymbol,
            currencyLocale:
                currencyOptions.find((opt: { value: string }) => opt.value === data.currencySymbol)?.currencyLocale ||
                '',
            termsOfUse: data.termsOfUse,
            eula: data.eula,
            dateFormat: data.dateFormat,
            logo: logoBase64 || '',
            secondaryLogo: secondaryLogoBase64 || '',
            ...{ themePalette: themeBase64 ? themeBase64 : JSON.stringify(globalConfig.themePalette) }
        };

        try {
            await dispatch(updateTenantInfo(requestedPayload)).unwrap();
        } catch (error) {
            // AEX-3363: tenant info changed in another tab — show the Refresh banner and keep the form.
            if (isStalePlanError(error)) {
                setIsStale(true);
                dispatch(updateGlobalLoaderState(false));
                return;
            }
        }
        setSavedValues(data);
        reset(data);
        // setLogoFile(null);
        // setSecondaryLogoFile(null);
        setThemeFile(null);

        dispatch(updateGlobalLoaderState(false));
    };

    const handleCancel = () => {
        reset(savedValues);
        setLogoFile(null);
        setSecondaryLogoFile(null);
        setLogoBase64(logo || '');
        setSecondaryLogoBase64(secondaryLogo || '');
        setThemeFile(null);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleLogoDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const file = event.dataTransfer.files?.[0];
        if (file) {
            if (file.type !== 'image/svg+xml') {
                alert('Please upload an SVG file');
                return;
            }
            if (file.size > 200 * 1024) {
                alert('File size must be less than 200KB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoFile(file);
                setLogoBase64((reader.result as string) || '');
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSecondaryLogoDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const file = event.dataTransfer.files?.[0];
        if (file) {
            if (file.type !== 'image/svg+xml') {
                alert('Please upload an SVG file');
                return;
            }
            if (file.size > 200 * 1024) {
                alert('File size must be less than 200KB');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setSecondaryLogoFile(file);
                setSecondaryLogoBase64((reader.result as string) || '');
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSecondaryLogoClick = () => {
        document.getElementById('secondary-logo-upload')?.click();
    };

    const handleSecondaryLogoDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleThemeDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const file = event.dataTransfer.files?.[0];
        if (file) {
            if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
                alert('Please upload a JSON file');
                return;
            }
            setThemeFile(file);
        }
    };

    const downloadSampleJSON = () => {
        const dataStr =
            'data:text/json;charset=utf-8,' +
            encodeURIComponent(JSON.stringify(globalConfig?.themePalette || getNewPalette(tenant), null, 2));
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute('href', dataStr);
        downloadAnchorNode.setAttribute('download', 'sampleColorSchema.json');
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    const deleteLogo = (type: 'primaryLogo' | 'secondaryLogo') => {
        if (type === 'primaryLogo') {
            setLogoFile(null);
            setLogoBase64('');
            const logoInput = document.getElementById('logo-upload') as HTMLInputElement | null;
            if (logoInput) {
                logoInput.value = '';
            }
        } else {
            setSecondaryLogoFile(null);
            setSecondaryLogoBase64('');
            const secondaryLogoInput = document.getElementById('secondary-logo-upload') as HTMLInputElement | null;
            if (secondaryLogoInput) {
                secondaryLogoInput.value = '';
            }
        }
    };

    const getLabelWithCloseIcon = (label: string, type: 'primaryLogo' | 'secondaryLogo') => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{label}</span>
            {!label ? (
                <></>
            ) : (
                <LogoContainer
                    onClick={(event) => {
                        event.stopPropagation();
                        deleteLogo(type);
                    }}
                >
                    <CloseIcon />
                </LogoContainer>
            )}
        </div>
    );

    const isPrimaryLogoMissing = !logoBase64;

    return (
        <StyledBox>
            {isStale && (
                <StalePlanBanner
                    onRefresh={refreshTenantInfo}
                    message={t('STALE_CONFIG_BANNER_MSG')}
                    refreshLabel={t('REFRESH_CONFIG')}
                />
            )}
            <FormWrapper onSubmit={handleSubmit(onSubmit)}>
                <FormRow container>
                    <FormField>
                        <Label variant="labels.large" label={t('TENANT_TYPE')} />
                        <Controller
                            name="tenantType"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                                }
                            }}
                            render={({ field: { onChange, value, onBlur } }) => (
                                <ComboSelect
                                    onSelectChange={onChange}
                                    onBlur={onBlur}
                                    selectedValue={value}
                                    placeholder={t('TEXT_SELECT')}
                                    options={tenantOptions}
                                    error={!!errors.tenantType}
                                    size="small"
                                />
                            )}
                        />
                    </FormField>

                    <FormField>
                        <Label variant="labels.large" label={t('CURRENCY')} />
                        <Controller
                            name="currencySymbol"
                            control={control}
                            rules={{
                                required: { value: true, message: t('TEXT_THIS_IS_REQUIRED_FIELD') }
                            }}
                            render={({ field: { onChange, value, onBlur } }) => (
                                <ComboSelect
                                    onSelectChange={onChange}
                                    onBlur={onBlur}
                                    selectedValue={
                                        currencyOptions.find((opt: { value: string }) => opt.value === value)?.label ||
                                        ''
                                    }
                                    placeholder={t('TEXT_SELECT')}
                                    options={currencyOptions.map((option: any) => ({
                                        ...option,
                                        label: t(option.label.toUpperCase())
                                    }))}
                                    error={!!errors.currencySymbol}
                                    size="small"
                                    renderValue={(selected) => {
                                        if (!selected) return <em>{t('TEXT_SELECT')}</em>;
                                        const selectedOption = currencyOptions.find(
                                            (opt: { value: string }) => opt.value === selected
                                        );
                                        return selectedOption
                                            ? t(selectedOption.label.toUpperCase())
                                            : t(selected.toUpperCase());
                                    }}
                                />
                            )}
                        />
                    </FormField>
                </FormRow>

                <FormRow container>
                    <FormField>
                        <Label variant="labels.large" label={t('AGREEMENTS')} />
                        <Controller
                            name="termsOfUse"
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                                <FormControlLabel
                                    ref={ref}
                                    onChange={onChange}
                                    checked={value}
                                    control={<Checkbox sx={{ fontSize: '20px', paddingY: 0, paddingX: '5px' }} />}
                                    label={
                                        <Typography variant="body1" sx={{ fontSize: '14px' }}>
                                            {t('TERMS_OF_USE')}
                                        </Typography>
                                    }
                                />
                            )}
                        />
                        <Controller
                            name="eula"
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                                <FormControlLabel
                                    ref={ref}
                                    onChange={onChange}
                                    checked={value}
                                    control={<Checkbox sx={{ fontSize: '20px', paddingY: 0, paddingX: '5px' }} />}
                                    label={
                                        <Typography variant="body1" sx={{ fontSize: '14px' }}>
                                            {t('EULA')}
                                        </Typography>
                                    }
                                />
                            )}
                        />
                    </FormField>
                    <FormField>
                        <Label variant="labels.large" label={t('DATE_FORMAT')} />
                        <Controller
                            name="dateFormat"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <RadioOption
                                    value={value}
                                    onRadioChange={(val) => onChange(val)}
                                    options={[
                                        { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
                                        { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' }
                                    ]}
                                    sx={{
                                        '.MuiFormControlLabel-root': {
                                            '& .MuiTypography-root': {
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                lineHeight: '1.25rem',
                                                margin: 0
                                            },
                                            '& .MuiButtonBase-root': {
                                                padding: '4px 4px 4px 10px'
                                            },
                                            marginBottom: '16px'
                                        }
                                    }}
                                />
                            )}
                        />
                    </FormField>
                </FormRow>

                <FormRow container>
                    {/* Primary logo */}
                    <FormField>
                        <Label variant="labels.large" label={t('UPLOAD_PRIMARY_COMPANY_LOGO_LABEL')} />
                        <HelperText label={t('UPLOAD_LOGO_HELPER_TEXT')} />
                        {/* <AdminUploadCompanyLogo formattedGlobal={globalConfig} setAddedFile={setLogoFile} /> */}
                        <input
                            type="file"
                            accept=".svg,image/svg+xml"
                            onChange={handleLogoUpload}
                            style={{ display: 'none' }}
                            id="logo-upload"
                        />
                        <UploadArea
                            onDragOver={handleDragOver}
                            onDrop={handleLogoDrop}
                            onClick={() => document.getElementById('logo-upload')?.click()}
                        >
                            {logoBase64 ? <Image src={logoBase64} /> : <UploadIcon />}
                            {logoFile && getLabelWithCloseIcon(logoFile.name, 'primaryLogo')}
                            {!logoFile &&
                                logoBase64 &&
                                getLabelWithCloseIcon(
                                    getFileInfoFromBase64(logoBase64, 'primaryLogo').name,
                                    'primaryLogo'
                                )}
                            {logoFile && logoBase64 && (
                                <span style={{ color: theme.palette.grey[400] }}>
                                    {getFileInfoFromFile(logoFile).size}
                                </span>
                            )}
                            {!logoFile && logoBase64 && (
                                <span style={{ color: theme.palette.grey[400] }}>
                                    {getFileInfoFromBase64(logoBase64, 'primaryLogo').size}
                                </span>
                            )}
                            {!logoBase64 && !logoFile && (
                                <UploadText>
                                    <span>{t('CLICK_TO_UPLOAD')}</span> {t('OR_DRAG_AND_DROP')}
                                </UploadText>
                            )}
                        </UploadArea>
                        {isPrimaryLogoMissing && (
                            <HelperText sx={{ color: 'error.main' }} label={t('PRIMARY_LOGO_REQUIRED_TEXT')} />
                        )}
                    </FormField>

                    {/* Secondary logo */}
                    <FormField>
                        <Label variant="labels.large" label={t('UPLOAD_SECONDARY_COMPANY_LOGO_LABEL')} />
                        <HelperText label={t('UPLOAD_LOGO_HELPER_TEXT')} />
                        {/* <AdminUploadCompanyLogo formattedGlobal={globalConfig} setAddedFile={setLogoFile} /> */}
                        <input
                            type="file"
                            accept=".svg,image/svg+xml"
                            onChange={handleSecondaryLogoUpload}
                            style={{ display: 'none' }}
                            id="secondary-logo-upload"
                        />
                        <UploadArea
                            onDragOver={handleSecondaryLogoDragOver}
                            onDrop={handleSecondaryLogoDrop}
                            onClick={handleSecondaryLogoClick}
                        >
                            {secondaryLogoBase64 ? <Image src={secondaryLogoBase64} /> : <UploadIcon />}
                            {secondaryLogoFile && getLabelWithCloseIcon(secondaryLogoFile.name, 'secondaryLogo')}
                            {!secondaryLogoFile &&
                                secondaryLogoBase64 &&
                                getLabelWithCloseIcon(
                                    getFileInfoFromBase64(secondaryLogoBase64, 'secondaryLogo').name,
                                    'secondaryLogo'
                                )}
                            {secondaryLogoFile && secondaryLogoBase64 && (
                                <span style={{ color: theme.palette.grey[400] }}>
                                    {getFileInfoFromFile(secondaryLogoFile).size}
                                </span>
                            )}
                            {!secondaryLogoFile && secondaryLogoBase64 && (
                                <span style={{ color: theme.palette.grey[400] }}>
                                    {getFileInfoFromBase64(secondaryLogoBase64, 'secondaryLogo').size}
                                </span>
                            )}
                            {!secondaryLogoBase64 && !secondaryLogoFile && (
                                <UploadText>
                                    <span>{t('CLICK_TO_UPLOAD')}</span> {t('OR_DRAG_AND_DROP')}
                                </UploadText>
                            )}
                        </UploadArea>
                    </FormField>
                </FormRow>

                <FormRow container>
                    <FormField>
                        <Label variant="labels.large" label={t('UPLOAD_THEME_FILE_LABEL')} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <HelperText label={t('UPLOAD_THEME_FILE_HELPER_TEXT')} />
                            <Box
                                component="a"
                                onClick={downloadSampleJSON}
                                sx={{
                                    fontSize: '12px',
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {t('DOWNLOAD_SAMPLE_COLOR_THEME_SCHEMA')}
                            </Box>
                        </Box>
                        <input
                            type="file"
                            accept=".json,application/json"
                            onChange={handleThemeUpload}
                            style={{ display: 'none' }}
                            id="theme-upload"
                        />
                        <UploadArea
                            onDragOver={handleDragOver}
                            onDrop={handleThemeDrop}
                            onClick={() => document.getElementById('theme-upload')?.click()}
                        >
                            <UploadIcon />
                            <UploadText>
                                <span>{t('CLICK_TO_UPLOAD')}</span> {t('OR_DRAG_AND_DROP')}
                            </UploadText>
                            {themeFile && (
                                <Typography variant="body2" sx={{ marginTop: 1, color: 'success.main' }}>
                                    {themeFile.name}
                                </Typography>
                            )}
                        </UploadArea>
                        {jsonError && <HelperText sx={{ color: 'error.main' }} label={jsonError} />}
                    </FormField>
                </FormRow>

                <ButtonGroup>
                    <BasicButton
                        type="submit"
                        variant="contained"
                        disabled={Object.keys(errors).length > 0 || isPrimaryLogoMissing}
                        sx={{ minWidth: '100px' }}
                    >
                        {t('BUTTON_SAVE')}
                    </BasicButton>
                    <BasicButton onClick={handleCancel} variant="outlined" sx={{ minWidth: '100px' }}>
                        {t('BUTTON_CANCEL')}
                    </BasicButton>
                </ButtonGroup>
            </FormWrapper>
        </StyledBox>
    );
};
